import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

import { FIREBASE_CONFIG } from '../config/Config';
import { storage } from '../shared/api/firebase/config';
import { logClientPortalEvent } from './portalEventLog';
import { generateConsentTermPDF } from './ConsentTermGenerator';
import {
  readPublishedDocument,
  readLatestPublishedDocumentInSeries,
  patchDocumentEverywhere,
  serverTimestamp,
} from '../services/ConsentRepository';

export { readPublishedDocument, readLatestPublishedDocumentInSeries };

const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

const PENDING = new Set(['pending_consent', 'pending', 'requested']);
// [UU-08 Fase F] 'signed' permanece em ACCEPTED (retro-compat: assinatura É positiva),
// mas tb entra no conjunto SIGNED próprio para precedência no descriptor visual.
const ACCEPTED = new Set(['accepted', 'consent_accepted', 'approved', 'granted', 'signed']);
const SIGNED = new Set(['signed']);
const DECLINED = new Set(['declined', 'consent_declined', 'rejected', 'refused']);
const REVIEW_PENDING = new Set(['review_pending']);

const clean = (value) => String(value || '').trim();
const lower = (value) => clean(value).toLowerCase();

export const normalizeDocumentConsentState = (row) => {
  const requiresConsent = Boolean(row?.requiresConsent);
  const consentStatus = lower(row?.consentStatus);
  const status = lower(row?.status);

  const isReviewPending = REVIEW_PENDING.has(consentStatus) || REVIEW_PENDING.has(status);
  const isPending = requiresConsent && !isReviewPending && (PENDING.has(consentStatus) || PENDING.has(status));
  const isAccepted = ACCEPTED.has(consentStatus) || ACCEPTED.has(status);
  const isSigned = SIGNED.has(consentStatus) || SIGNED.has(status); // [UU-08 Fase F] precedência sobre isAccepted no descriptor
  const isDeclined = DECLINED.has(consentStatus) || DECLINED.has(status);
  const isResolved = isAccepted || isDeclined || isReviewPending;

  return {
    requiresConsent,
    consentStatus,
    status,
    isPending,
    isReviewPending,
    isAccepted,
    isSigned,
    isDeclined,
    isResolved,
    canRequest: Boolean(clean(row?.id || row?.documentId)) && !isPending && !isResolved,
  };
};

export const getConsentActionDescriptor = (row) => {
  const state = normalizeDocumentConsentState(row);
  // [UU-08 Fase F] isSigned tem precedência sobre isAccepted no descriptor
  if (state.isSigned) {
    return {
      ...state,
      label: '🔒 Assinado Digitalmente',
      disabled: true,
      className: 'border-blue-200 bg-blue-50 text-blue-700 cursor-not-allowed shadow-sm',
    };
  }
  if (state.isAccepted) {
    return {
      ...state,
      label: 'Consentimento Confirmado',
      disabled: true,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700 cursor-not-allowed shadow-sm',
    };
  }

  if (state.isDeclined) {
    return {
      ...state,
      label: 'Consentimento Recusado',
      disabled: true,
      className: 'border-red-200 bg-red-50 text-red-700 cursor-not-allowed shadow-sm',
    };
  }

  if (state.isReviewPending) {
    return {
      ...state,
      label: 'Em Análise pelo Escritório',
      disabled: true,
      className: 'border-yellow-300 bg-yellow-50 text-yellow-700 cursor-not-allowed shadow-sm',
    };
  }

  if (state.isPending) {
    return {
      ...state,
      label: 'Consentimento Solicitado',
      disabled: true,
      className: 'border-amber-200 bg-amber-50 text-amber-700 cursor-not-allowed shadow-sm',
    };
  }

  return {
    ...state,
    label: 'Solicitar Consentimento',
    disabled: !state.canRequest,
    className: 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 shadow-sm',
  };
};

const resolveDocumentTitle = (data) => {
  return clean(data?.title || data?.name || data?.fileName || data?.filename) || 'Documento Publicado';
};

export const requestDocumentConsent = async ({ documentId, actorName = 'Escritório' }) => {
  const loaded = await readPublishedDocument(documentId);
  if (!loaded?.data) {
    return { ok: false, reason: 'Documento não localizado.' };
  }

  const initialClientId = clean(loaded.data?.clientId || loaded.data?.context?.primaryEntityId);
  if (!initialClientId) {
    throw new Error('Não é possível solicitar consentimento para um documento órfão. Vincule-o a um cliente primeiro.');
  }

  const state = normalizeDocumentConsentState(loaded.data);
  if (state.isPending) return { ok: false, reason: 'Consentimento já solicitado.', data: loaded.data };
  if (state.isResolved) return { ok: false, reason: 'Documento já possui resposta de consentimento.', data: loaded.data };

  const patch = {
    requiresConsent: true,
    consentStatus: 'pending_consent',
    status: 'pending_consent',
    publishType: 'consent',
    consentRequestedAt: serverTimestamp(),
    consentRequestedBy: clean(actorName) || 'Escritório',
    updatedAt: serverTimestamp(),
  };

  const wrote = await patchDocumentEverywhere(documentId, patch);
  if (!wrote) return { ok: false, reason: 'Não foi possível atualizar o documento.' };

  const nextData = { ...loaded.data, ...patch, id: clean(documentId) };
  const clientId = clean(nextData?.clientId || nextData?.context?.primaryEntityId);
  const fileName = resolveDocumentTitle(nextData);

  if (clientId) {
    try {
      await logClientPortalEvent({
        clientId,
        type: 'DOCUMENT_CONSENT_REQUESTED',
        actorName,
        category: 'SYSTEM',
        payload: {
          documentId: clean(documentId),
          fileName,
          fileUrl: clean(nextData?.fileUrl || nextData?.url || nextData?.downloadUrl),
          requiresConsent: true,
          consentStatus: 'pending_consent',
          text: `Consentimento informado solicitado para o documento: ${fileName}`,
        },
        isBot: false,
      });
    } catch (e) {
      void e;
    }
  }

  return { ok: true, data: nextData };
};

export const resolveDocumentConsentFromClient = async ({ documentId, decision = 'accepted', actorName = 'Cliente' }) => {
  const loaded = await readPublishedDocument(documentId);
  if (!loaded?.data) return { ok: false, reason: 'Documento não localizado.' };

  const initialClientId = clean(loaded.data?.clientId || loaded.data?.context?.primaryEntityId);
  if (!initialClientId) {
    throw new Error('Não é possível registrar consentimento para um documento órfão. Vincule-o a um cliente primeiro.');
  }

  const docCategory = clean(loaded.data?.category || loaded.data?.classification?.category);
  const docModule = clean(loaded.data?.module);
  const catLower = docCategory.toLowerCase();
  const isSignatureDoc = catLower.includes('procura') || catLower.includes('contrat');
  const isPetition = catLower.includes('peti') || lower(loaded.data?.type) === 'petition' || lower(docModule) === 'petitions';

  const safeDecision = lower(decision) === 'declined' ? 'declined' : 'accepted';
  const fileName = resolveDocumentTitle(loaded.data);

  const consentRecord = {
    decision: safeDecision,
    actionType: isSignatureDoc ? 'signature' : (isPetition ? 'petition_consent' : 'informed_consent'),
    actorName: clean(actorName) || 'Cliente Autenticado',
    decidedAt: serverTimestamp(),
    decidedAtIso: new Date().toISOString(),
    documentId: clean(documentId),
    documentTitle: fileName,
    documentCategory: docCategory || 'Documento',
    userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '',
    ipNote: 'Ação autenticada registrada via Portal do Cliente RDA',
  };

  const patch = safeDecision === 'accepted'
    ? {
        consentStatus: 'accepted',
        status: 'PUBLISHED',
        consentRespondedAt: serverTimestamp(),
        consentAcceptedAt: serverTimestamp(),
        consentAcceptedBy: clean(actorName) || 'Cliente',
        consentRecord,
        updatedAt: serverTimestamp(),
      }
    : {
        consentStatus: 'declined',
        status: 'CONSENT_DECLINED',
        consentRespondedAt: serverTimestamp(),
        consentDeclinedAt: serverTimestamp(),
        consentDeclinedBy: clean(actorName) || 'Cliente',
        consentRecord,
        updatedAt: serverTimestamp(),
      };

  const wrote = await patchDocumentEverywhere(documentId, patch);
  if (!wrote) return { ok: false, reason: 'Não foi possível registrar a resposta do consentimento.' };

  const nextData = { ...loaded.data, ...patch, id: clean(documentId) };
  const clientId = clean(nextData?.clientId || nextData?.context?.primaryEntityId);

  // [UU-08 Fase F GAP-3] Gera Termo de Consentimento em PDF (silent-fail não-bloqueante).
  // Se falhar, o consentimento ainda vale — os campos em Firestore são a fonte da verdade.
  if (safeDecision === 'accepted' && appId) {
    try {
      const termActionType = isSignatureDoc ? 'signature' : (isPetition ? 'petition_consent' : 'informed_consent');
      const termBlob = await generateConsentTermPDF({
        clientName: clean(actorName) || 'Cliente',
        clientId: clientId || initialClientId,
        documentTitle: fileName,
        documentId: clean(documentId),
        documentHash: clean(loaded.data?.authenticityHash || loaded.data?.hash),
        documentCategory: docCategory || 'Documento',
        consentDecision: 'accepted',
        consentTimestamp: new Date().toISOString(),
        actionType: termActionType,
        userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '',
        ipNote: 'Ação autenticada via Portal do Cliente RDA',
      });
      const termPath = `artifacts/${appId}/public/documents/consent-terms/${clientId || 'unlinked'}/termo_${clean(documentId)}_${Date.now()}.pdf`;
      const termRef = storageRef(storage, termPath);
      await uploadBytes(termRef, termBlob, { contentType: 'application/pdf' });
      const termUrl = await getDownloadURL(termRef);
      await patchDocumentEverywhere(documentId, {
        consentTermPdfUrl: termUrl,
        consentTermStoragePath: termPath,
        consentTermGeneratedAt: serverTimestamp(),
      });
      nextData.consentTermPdfUrl = termUrl;
      nextData.consentTermStoragePath = termPath;
    } catch (termErr) {
      // Silent-fail: consentimento já foi registrado; termo é complemento probatório.
      console.warn('[RDA] UU-08 Fase F — falha ao gerar termo de consentimento (não-bloqueante):', termErr);
    }
  }

  // Propagate consent to parent document (e.g., petition linked via parentDocumentId)
  const parentDocId = clean(loaded.data?.parentDocumentId);
  if (parentDocId) {
    try {
      const parentPatch = safeDecision === 'accepted'
        ? { consentStatus: 'accepted', updatedAt: serverTimestamp() }
        : { consentStatus: 'declined', updatedAt: serverTimestamp() };
      await patchDocumentEverywhere(parentDocId, parentPatch);
    } catch (parentErr) {
      console.warn('[RDA] Propagação de consentimento para documento pai falhou (não-bloqueante):', parentErr);
    }
  }

  const eventTypeAccepted = isSignatureDoc
    ? 'CLIENT_SIGNATURE_REGISTERED'
    : isPetition
      ? 'PETITION_CONSENT_ACCEPTED'
      : 'CLIENT_CONSENT_ACCEPTED';
  const eventTypeDeclined = isSignatureDoc
    ? 'CLIENT_SIGNATURE_DECLINED'
    : isPetition
      ? 'PETITION_CONSENT_DECLINED'
      : 'CLIENT_CONSENT_DECLINED';
  const labelAccepted = isSignatureDoc
    ? `Assinatura eletrônica registrada para o documento: ${fileName}`
    : isPetition
      ? `Petição aprovada pelo cliente para protocolo judicial: ${fileName}`
      : `Consentimento aceito pelo cliente para o documento: ${fileName}`;
  const labelDeclined = isSignatureDoc
    ? `Assinatura recusada pelo cliente para o documento: ${fileName}`
    : isPetition
      ? `Petição recusada pelo cliente — protocolo judicial suspenso: ${fileName}`
      : `Consentimento recusado pelo cliente para o documento: ${fileName}`;

  if (clientId) {
    try {
      await logClientPortalEvent({
        clientId,
        type: safeDecision === 'accepted' ? eventTypeAccepted : eventTypeDeclined,
        actorName,
        category: 'SYSTEM',
        payload: {
          documentId: clean(documentId),
          fileName,
          consentStatus: safeDecision,
          consentRecord,
          workspaceLabel: docCategory || (isPetition ? 'Petição' : 'Procuração'),
          module: docModule || (isPetition ? 'petitions' : 'procurations'),
          text: safeDecision === 'accepted' ? labelAccepted : labelDeclined,
        },
      });
    } catch (e) {
      void e;
    }
  }

  return { ok: true, data: nextData };
};

export const approveSignedDocument = async ({ documentId, clientId, actorName = 'Rodrigo Demeterco' }) => {
  const safeDocId = clean(documentId);
  const safeClientId = clean(clientId);
  if (!safeDocId) return { ok: false, reason: 'ID do documento não informado.' };

  const loaded = await readPublishedDocument(safeDocId);
  if (!loaded?.data) return { ok: false, reason: 'Documento não localizado.' };

  const resolvedClientId = safeClientId || clean(loaded.data?.clientId || loaded.data?.context?.primaryEntityId);
  const fileName = resolveDocumentTitle(loaded.data);
  const docCategory = clean(loaded.data?.category || loaded.data?.classification?.category);
  const catLower = docCategory.toLowerCase();
  const isSignatureDoc = catLower.includes('procura') || catLower.includes('contrat');

  const patch = {
    consentStatus: 'signed',
    status: 'SIGNED',
    approvedAt: serverTimestamp(),
    approvedBy: clean(actorName) || 'Escritório',
    updatedAt: serverTimestamp(),
  };

  const wrote = await patchDocumentEverywhere(safeDocId, patch);
  if (!wrote) return { ok: false, reason: 'Não foi possível aprovar o documento.' };

  if (resolvedClientId) {
    try {
      await logClientPortalEvent({
        clientId: resolvedClientId,
        type: isSignatureDoc ? 'CLIENT_SIGNATURE_APPROVED' : 'CLIENT_CONSENT_APPROVED',
        actorName,
        category: 'SYSTEM',
        payload: {
          documentId: safeDocId,
          fileName,
          consentStatus: 'signed',
          workspaceLabel: docCategory || 'Documento',
          text: isSignatureDoc
            ? `Assinatura validada e aprovada pelo escritório para o documento: ${fileName}`
            : `Consentimento aprovado pelo escritório para o documento: ${fileName}`,
        },
      });
    } catch (e) {
      void e;
    }
  }

  return { ok: true, data: { ...loaded.data, ...patch, id: safeDocId } };
};

export const rejectSignedDocument = async ({ documentId, clientId, actorName = 'Rodrigo Demeterco', reason = '' }) => {
  const safeDocId = clean(documentId);
  const safeClientId = clean(clientId);
  if (!safeDocId) return { ok: false, reason: 'ID do documento não informado.' };

  const loaded = await readPublishedDocument(safeDocId);
  if (!loaded?.data) return { ok: false, reason: 'Documento não localizado.' };

  const resolvedClientId = safeClientId || clean(loaded.data?.clientId || loaded.data?.context?.primaryEntityId);
  const fileName = resolveDocumentTitle(loaded.data);
  const docCategory = clean(loaded.data?.category || loaded.data?.classification?.category);

  const patch = {
    consentStatus: 'pending_consent',
    status: 'pending_consent',
    rejectedAt: serverTimestamp(),
    rejectedBy: clean(actorName) || 'Escritório',
    rejectionReason: clean(reason) || 'Assinatura não validada pelo escritório.',
    updatedAt: serverTimestamp(),
  };

  const wrote = await patchDocumentEverywhere(safeDocId, patch);
  if (!wrote) return { ok: false, reason: 'Não foi possível rejeitar o documento.' };

  if (resolvedClientId) {
    try {
      await logClientPortalEvent({
        clientId: resolvedClientId,
        type: 'CLIENT_SIGNATURE_REJECTED',
        actorName,
        category: 'SYSTEM',
        payload: {
          documentId: safeDocId,
          fileName,
          consentStatus: 'pending_consent',
          rejectionReason: clean(reason) || 'Assinatura não validada pelo escritório.',
          workspaceLabel: docCategory || 'Documento',
          text: `Assinatura rejeitada pelo escritório para o documento: ${fileName}. Motivo: ${clean(reason) || 'Não validada.'}`,
        },
      });
    } catch (e) {
      void e;
    }
  }

  return { ok: true, data: { ...loaded.data, ...patch, id: safeDocId } };
};
